import { useSearch } from "@tanstack/react-router";
import { useGetConversionStateQuery } from "api/TenantSlug/ConversionState/hooks.ts";
import { LogCustomParametersSection } from "components/Drawers/Logs/shared/LogCustomParametersSection.tsx";
import { LogTransactionsSection } from "components/Drawers/Logs/shared/LogTransactionsSection.tsx";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList.tsx";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { DrawerId } from "types/models.ts";

export const LogConversionDrawerDetail: FC = () => {
    const { t } = usePaldockTranslation("drawers.log-conversion-detail");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tBase } = usePaldockTranslation();
    const { t: tGL } = usePaldockTranslation("drawers.general-labels");
    const id = DrawerId.LogConversionDetail;

    const params: { logConversionDetail?: string } = useSearch({ strict: false });

    const getConversionQuery = useGetConversionStateQuery(
        params?.logConversionDetail || "",
        {
            filter: { include: "country,owner,offer,advertiser,payouts,payouts.currency,payouts.commission,payouts.conversionState,payouts.conversionState.advertiser,payouts.conversionState.cascade,cascade" },
        },
        {
            enabled: !!params?.logConversionDetail,
        }
    );

    const { conversion } = getConversionQuery.data || {};
    const transactions = conversion?.payouts ?? [];
    const loading = getConversionQuery.isLoading;

    const generalInfoList = PropertiesList.create(conversion, [
        {
            term: tBase("forms.general-fields.created_at.label"),
            description: ({ created_at }) => created_at ? <DateTimeCell value={created_at} /> : "-",
        },
        {
            term: tBase("tables.general-columns.country"),
            description: ({ country }) => country?.name,
        },
        {
            term: tBase("tables.general-columns.state"),
            description: ({ state }) => state ? tGL("ok") : tGL("fail"),
        },
        {
            term: tBase("forms.general-fields.advertiser.label"),
            description: ({ advertiser }) => advertiser?.nickname || advertiser?.email || undefined,
            render: ({ advertiser }) => <EntityLinkCell.Advertiser entity={advertiser} />,
        },
        {
            term: tBase("forms.general-fields.offer.label"),
            description: ({ offer }) => offer?.title,
            render: ({ offer }) => <EntityLinkCell.Offer entity={offer} />,
        },
        {
            term: tBase("forms.general-fields.affiliate.label"),
            description: ({ owner }) => owner?.nickname || owner?.email || undefined,
            render: ({ owner }) => <EntityLinkCell.Owner entity={owner} />,
        },
        {
            term: tBase("tables.conversion-logs.columns.conversion_type"),
            description: ({ type }) => type || "-",
        },
        {
            term: tBase("tables.conversion-logs.columns.method"),
            description: ({ origin }) => origin || "-",
        },
        {
            term: tBase("forms.general-fields.channel.label"),
            description: ({ cascade }) => cascade?.visible_title ?? cascade?.title ?? "-",
        },
        {
            term: tBase("forms.general-fields.conversion_id.label"),
            description: ({ eid }) => eid,
            render: ({ eid }) => eid ? <ShortenedIdWithCopy id={eid} charactersBetweenDots={35} /> : "-",
        },
        {
            term: tBase("tables.general-columns.origin_id"),
            description: ({ origin_eid }) => origin_eid,
            render: ({ origin_eid }) => origin_eid ? <ShortenedIdWithCopy id={origin_eid} charactersBetweenDots={35} /> : "-",
        },
        {
            term: tBase("tables.conversion-logs.columns.send_id"),
            description: ({ send_eid }) => send_eid,
            render: ({ send_eid }) => send_eid ? <ShortenedIdWithCopy id={send_eid} charactersBetweenDots={35} /> : "-",
        },
        {
            term: tBase("forms.general-fields.affiliate_cid.label"),
            description: ({ owner_custom_id }) => owner_custom_id || "-",
            render: ({ owner_custom_id }) => owner_custom_id ? <ShortenedIdWithCopy id={owner_custom_id} charactersBetweenDots={35} /> : "-",
        },
        {
            term: tBase("tables.general-columns.external_id"),
            description: ({ advertiser_custom_id }) => advertiser_custom_id || "-",
            render: ({ advertiser_custom_id }) => advertiser_custom_id ? <ShortenedIdWithCopy id={advertiser_custom_id} charactersBetweenDots={35} /> : "-",
        },
        {
            term: tBase("tables.general-columns.source"),
            description: ({ source }) => source || "-",
        },
    ]);

    if (!params?.logConversionDetail) {
        return null;
    }

    return (
        <Drawer
            loading={loading}
            drawerId={id}
            searchParamsToClean={["logConversionDetail"]}
            title={<DrawerTitle mainTitle={t("title")} />}
        >
            <DrawerContent>
                <DrawerCollapsible title={tC("general")}>
                    <PropertiesList list={generalInfoList} />
                </DrawerCollapsible>

                <LogCustomParametersSection conversion={conversion} />

                <LogTransactionsSection payouts={transactions} loading={loading} />
            </DrawerContent>
        </Drawer>
    );
};
